import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AnimatePresence, motion } from "framer-motion";

import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";

import { anim } from "../Animations/utils";

import CameraCapture from "./PhotoCapture";

import FileUploadProvider from "../../context/FileUpload";

const animatePhoto = {
  initial: { x: "100%" },
  animate: {
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
  exit: {
    x: "100%",
    transition: { duration: 0.4, ease: [0.165, 0.84, 0.44, 1] },
  },
};

interface PhotoDialogProps {
  onClose: () => void;
  onAccept: (photo: string) => void;
  required?: boolean;
}

const PhotoDialog = (props: PhotoDialogProps) => {
  const { onClose, onAccept, required } = props;

  const [photoCapture, setPhotoCapture] = useState<boolean>(false);

  const { t } = useTranslation(["photo"]);

  return (
    <AnimatePresence mode="wait">
      <Dialog
        fullScreen
        open
        transitionDuration={0}
        component={motion.div}
        {...anim(animatePhoto)}
      >
        <DialogContent
          dividers
          className="p-0 d-flex align-items-center justify-content-center text-center"
        >
          {!photoCapture ? (
            <div className="d-flex flex-column">
              <div className="p-5">
                <DialogContentText
                  className="p-5 text-center fw-semibold"
                  style={{ fontSize: "4.5rem", color: "#000000" }}
                >
                  {t(`photo:${required ? "required" : "optional"}.title`)}
                </DialogContentText>
                <DialogContentText
                  className="p-5 text-center"
                  style={{ fontSize: "3.5rem", color: "#000000" }}
                >
                  {t(
                    `photo:${
                      required ? "required" : "optional"
                    }.securityNotice`,
                  )}
                </DialogContentText>
                <DialogContentText
                  className="p-4 text-center"
                  style={{ fontSize: "3.5rem", color: "#000000" }}
                >
                  {t(
                    `photo:${required ? "required" : "optional"}.instructions`,
                  )}
                </DialogContentText>
                <DialogContentText
                  className="p-4 text-center"
                  style={{ fontSize: "3.5rem", color: "#000000" }}
                >
                  {t(`photo:${required ? "required" : "optional"}.privacyInfo`)}
                </DialogContentText>
                <DialogContentText
                  className="p-5 text-center"
                  style={{ fontSize: "4.5rem", color: "#000000" }}
                >
                  {t(
                    `photo:${
                      required ? "required" : "optional"
                    }.closingMessage`,
                  )}
                </DialogContentText>
              </div>
            </div>
          ) : (
            <FileUploadProvider>
              <CameraCapture onAccept={onAccept} />
            </FileUploadProvider>
          )}
        </DialogContent>
        <DialogActions className="p-5">
          <Button variant="contained" className="px-5 me-5 w" onClick={onClose}>
            {t(`photo:button.${required ? "cancel" : "skip"}`)}
          </Button>
          {!photoCapture && (
            <Button
              variant="contained"
              className="px-5"
              onClick={() => {
                setPhotoCapture(true);
              }}
            >
              {t(`photo:button.${required ? "next" : "accept"}`)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AnimatePresence>
  );
};

export default PhotoDialog;
